import { create } from 'zustand';

/** Mirrors the API's `EntitlementError`. Only the two entitlement codes reach this store. */
export interface EntitlementErrorPayload {
  code: 'USAGE_LIMIT_EXCEEDED' | 'FEATURE_NOT_ENTITLED';
  featureKey?: string;
  message?: string;
  /** Activation blocked by N questions whose type the plan lacks (remove them or upgrade). */
  questionCount?: number;
  upgrade?: { currentPlan?: string; limit?: number; used?: number };
}

interface UpgradeDialogState {
  isOpen: boolean;
  payload: EntitlementErrorPayload | null;
  open: (payload: EntitlementErrorPayload) => void;
  close: () => void;
}

/** Kept OUTSIDE reactive state so updating it never re-renders. Identical errors in the window are ignored. */
const DEDUPE_WINDOW_MS = 4000;
let lastKey = '';
let lastAt = 0;

/** Exactly one dialog instance, so concurrent 403s collapse into one prompt. Pure UI state — never persist. */
export const useUpgradeDialogStore = create<UpgradeDialogState>((set) => ({
  isOpen: false,
  payload: null,
  open: (payload) => {
    const key = `${payload.code}:${payload.featureKey ?? ''}`;
    const now = Date.now();
    if (key === lastKey && now - lastAt < DEDUPE_WINDOW_MS) return;
    lastKey = key;
    lastAt = now;
    set({ isOpen: true, payload });
  },
  close: () => {
    // Reset the dedupe latch so a deliberate reopen after dismiss isn't
    // swallowed; an automatic burst is still collapsed while the dialog is open.
    lastKey = '';
    lastAt = 0;
    set({ isOpen: false, payload: null });
  },
}));
